import { useEffect, useState } from "react";
import { fetchWithTimeout } from "../../App";
import "../../App.css";

const API_URL = process.env.REACT_APP_API_URL;

function Dashboard() {
  const [orders, setOrders] = useState([]);
  const [menu, setMenu] = useState([]);
  const [locations, setLocations] = useState([]);

  const fetchOrders = async () => {
    try {
      const res = await fetchWithTimeout(`${API_URL}/api/admin/orders`, {
        credentials: "include",
      });
      const data = await res.json();
      if (Array.isArray(data)) setOrders(data);
    } catch (err) {
      console.error("Error fetching orders:", err);
    }
  };

  const fetchMenu = async () => {
    try {
      const res = await fetchWithTimeout(`${API_URL}/menu`);
      const data = await res.json();
      if (Array.isArray(data)) setMenu(data);
    } catch (err) {
      console.error("Error fetching menu:", err);
    }
  };

  const fetchLocations = async () => {
    try {
      const res = await fetchWithTimeout(`${API_URL}/locations`);
      const data = await res.json();
      if (Array.isArray(data)) setLocations(data);
    } catch (err) {
      console.error("Error fetching locations:", err);
    }
  };

  useEffect(() => {
    Promise.allSettled([fetchMenu(), fetchOrders(), fetchLocations()]);
  }, []);

  const priceMap = Object.fromEntries(menu.map((m) => [m.name, parseFloat(m.price)]));
  const deliveryMap = Object.fromEntries(locations.map((l) => [l.name, parseFloat(l.delivery_fee)]));

  const totalOrders = orders.reduce((sum, o) => sum + o.quantity, 0);
  const pendingOrders = orders.filter(o => o.status === "Pending").reduce((sum, o) => sum + o.quantity, 0);
  const readyOrders = orders.filter(o => o.status === "Ready").reduce((sum, o) => sum + o.quantity, 0);
  const completedOrders = orders.filter(o => o.status === "Completed").reduce((sum, o) => sum + o.quantity, 0);
  // Group by order_token so delivery fee is counted once per order, not per item row
  const groupedTokens = orders.reduce((acc, row) => {
    const token = row.order_token || `solo-${row.id}`;
    if (!acc[token]) acc[token] = { location: row.location, rows: [] };
    acc[token].rows.push(row);
    return acc;
  }, {});

  const totalRevenue = Object.values(groupedTokens).reduce((sum, g) => {
    const itemsTotal = g.rows.reduce((s, r) => {
      const price = r.item_price != null && parseFloat(r.item_price) > 0
        ? parseFloat(r.item_price)
        : (priceMap[r.item] || 0);
      return s + price * r.quantity;
    }, 0);
    const deliveryFee = g.rows[0]?.delivery_fee != null
      ? parseFloat(g.rows[0].delivery_fee)
      : (deliveryMap[g.location] || 0);
    const discountTotal = g.rows.reduce((s, r) => s + parseFloat(r.discount_amount || 0), 0);
    return sum + itemsTotal + deliveryFee - discountTotal;
  }, 0);

  const Card = ({ title, value, accent }) => (
    <div style={{
      background: "var(--warm-white)",
      border: "1px solid var(--border)",
      padding: "1.5rem",
      borderRadius: "16px",
      minWidth: "160px",
      flex: 1,
      boxShadow: "0 4px 24px rgba(92, 51, 23, 0.08)",
      borderTop: `4px solid ${accent || "var(--brown)"}`,
    }}>
      <p style={{ margin: 0, fontSize: "0.82rem", color: "var(--brown-mid)", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.04em", fontFamily: "'Lato', sans-serif" }}>
        {title}
      </p>
      <p style={{ fontSize: "2rem", fontWeight: "bold", margin: "0.5rem 0 0", color: "var(--text-main)", fontFamily: "'Playfair Display', Georgia, serif" }}>
        {value}
      </p>
    </div>
  );

  return (
    <div>
      <h1 className="admin-page-heading">📊 Dashboard</h1>
      <p className="admin-page-sub">Your kitchen at a glance</p>

      <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
        <Card title="Total Revenue (RM)" value={`RM ${totalRevenue.toFixed(2)}`} accent="var(--success)" />
        <Card title="Total Items Ordered" value={totalOrders} accent="var(--orange)" />
        <Card title="Pending Items" value={pendingOrders} accent="var(--gold)" />
        <Card title="Ready Items" value={readyOrders} accent="var(--success)" />
        <Card title="Completed Items" value={completedOrders} accent="var(--text-muted)" />
      </div>
    </div>
  );
}

export default Dashboard;